import React from "react";
import classNames from "classnames";

import { valueToString } from "helpers/Helpers";
import PaymentMethodIcon from "components/paymentMethodIcon/PaymentMethodIcon";

import Checkbox from "components/form/checkbox/Checkbox";
import { Badge } from "components/badge/Badge";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { ButtonNew } from "components/buttonNew";

type CreditCardTabProps = {
  card: ICreditCard;
  isSelected: boolean;
  changeCard?: boolean;
  onChangeCard?: () => void;
  onRemove?: () => void;
  setSelectedCard: (paymentMethodId: number) => void;
};

export default function CreditCardTab(props: CreditCardTabProps) {
  const { card, isSelected, changeCard = false, onChangeCard, onRemove } = props;
  const { t } = useTranslation();
  const isExpired = card.status === "expired";

  function handleCreditCardSelect(
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLElement, MouseEvent>,
    paymentId: number,
  ) {
    event.preventDefault();
    if (isExpired) {
      return;
    }
    props.setSelectedCard(paymentId);
  }

  return (
    <div
      onClick={event => handleCreditCardSelect(event, card.id)}
      className={classNames("ui-checkbox-group-item", {
        "ui-checkbox-group-item_selected": isSelected && !isExpired && !changeCard && !onRemove,
        "ui-checkbox-group-item_attached": isSelected && !isExpired && !changeCard && !!onRemove,
        "ui-checkbox-group-item_held": isSelected && changeCard && !isExpired,
        "cursor-not-allowed opacity-50": isExpired,
      })}
    >
      <div className="ui-checkbox-group-item-content">
        <div className="ui-checkbox-group-item-lead">
          <PaymentMethodIcon brand={card.brand} size="md" />
        </div>
        <div className="ui-checkbox-group-item-text">
          <p className="text-sm text-medium text-gray-700">
            {valueToString(card.brand, "capitalize")} •{card.last4}
            {isExpired && (
              <Badge className="ml-2" type="error" size="small">
                {t("Expired")}
              </Badge>
            )}
          </p>
          <p className="text-sm text-regular text-gray-600">
            {t("Expiry")} {card.expiry}
          </p>
          {onRemove ? (
            <ButtonNew
              className="ui-checkbox-group-item-remove"
              type="text"
              size="small"
              onClick={event => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <FontAwesomeIcon icon={faTrash} /> {t("Remove card from tab")}
            </ButtonNew>
          ) : null}
        </div>
      </div>
      {!changeCard ? (
        <Checkbox
          size="medium"
          value={card.id}
          checked={isSelected}
          disabled={isExpired}
          onChange={event => handleCreditCardSelect(event, card.id)}
        />
      ) : (
        <ButtonNew
          type="secondary"
          size="small"
          onClick={event => {
            event.stopPropagation();
            if (onChangeCard) {
              onChangeCard();
              return;
            }
            handleCreditCardSelect(event, card.id);
          }}
        >
          {t("Change card")}
        </ButtonNew>
      )}
    </div>
  );
}
